/**
 * Active birth — the chart currently under study across pages.
 *
 * ◆ Opening `#/kundali/<id>` (or any `#/<tool>/<id>`) pins that saved record so
 * Daśā, Reading, Guṇa Milan, etc. all seed the same birth without re-entry.
 * Kept in sessionStorage for this tab; IndexedDB remains the durable save.
 *
 * © Manish Jagdish Thatte
 */

import { get } from './db.js';
import { setSetting } from '../store.js';

const KEY = 'shastra.activeBirth.v1';

/** Routes that take an optional saved-chart id and seed the birth form. */
export const CHART_TOOL_ROUTES = Object.freeze([
  { route: 'kundali', label: 'Kundali' },
  { route: 'reading', label: 'Reading' },
  { route: 'dasha', label: 'Daśā' },
  { route: 'varga', label: 'Vargas' },
  { route: 'strength', label: 'Strength' },
  { route: 'yogas', label: 'Yogas' },
  { route: 'transit', label: 'Gochara' },
  { route: 'refinements', label: 'Refinements' },
  { route: 'tajaka', label: 'Tājaka' },
  { route: 'longevity', label: 'Longevity' },
  { route: 'remedies', label: 'Remedies' },
  { route: 'rectify', label: 'Rectify' },
  { route: 'matching', label: 'Guṇa Milan' },
  { route: 'correlation', label: 'Correlation' },
]);

export const CHART_TOOL_ROUTE_SET = Object.freeze(
  new Set(CHART_TOOL_ROUTES.map((r) => r.route)),
);

export function getActiveBirth() {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) || 'null');
  } catch {
    return null;
  }
}

/** Pin a saved-chart-shaped record as the session's active birth. */
export function setActiveBirth(rec) {
  if (!rec?.id || !rec.localISO) return;
  const slim = {
    id: rec.id,
    name: rec.name || rec.place?.name || rec.id,
    localISO: rec.localISO,
    place: rec.place || null,
    settings: rec.settings || null,
    input: rec.input || null,
    offsetMinutes: rec.offsetMinutes ?? null,
    offsetSource: rec.offsetSource ?? null,
    savedAt: rec.savedAt || null,
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(slim));
  } catch {
    /* private mode — in-memory only this turn */
  }
  return slim;
}

export function clearActiveBirth() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Map a saved record onto <input-datetime>.apply fields. */
export function fieldsFromSaved(rec) {
  const iso = rec?.localISO;
  if (!iso || typeof iso !== 'string') return null;
  const m = /^(-?\d{4,})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/.exec(iso);
  if (!m) return null;
  const timeKnown = rec.input?.timeKnown !== false;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4]),
    minute: Number(m[5]),
    second: Number(m[6]),
    timeKnown,
    calendar: 'auto',
    era: 'ce',
  };
}

/** Apply saved settings that are still valid; ignore junk. */
export function applySavedSettings(rec) {
  const s = rec?.settings || {};
  for (const key of ['ayanamsa', 'houseSystem', 'nodeMode']) {
    if (s[key] == null) continue;
    try {
      setSetting(key, s[key]);
    } catch (err) {
      console.warn(`[active-birth] setting "${key}" ignored:`, err.message);
    }
  }
}

/**
 * Load a chart id from IndexedDB into datetime + place-picker controls.
 * Pins it as active. Returns the record or null.
 */
export async function applySavedChart(id, dt, pp) {
  const rec = await get('charts', id);
  if (!rec) return null;
  applySavedSettings(rec);
  const fields = fieldsFromSaved(rec);
  if (!fields) return null;
  if (rec.place) {
    pp.forLocalISO = rec.localISO || null;
    pp.choose(rec.place);
  }
  dt.apply(fields);
  setActiveBirth(rec);
  return rec;
}

/** Apply the session active birth into controls (no IDB round-trip). */
export function applyActiveBirth(dt, pp) {
  const rec = getActiveBirth();
  if (!rec) return null;
  applySavedSettings(rec);
  const fields = fieldsFromSaved(rec);
  if (!fields) return null;
  if (rec.place) {
    pp.forLocalISO = rec.localISO || null;
    pp.choose(rec.place);
  }
  dt.apply(fields);
  return rec;
}

/**
 * Tool strip for the active chart. Links keep the chart id in the hash so
 * each page can re-seed from IndexedDB.
 */
export function chartToolsNav({ current = '', chartId = null } = {}) {
  const id = chartId || getActiveBirth()?.id || null;
  const active = getActiveBirth();
  const nav = document.createElement('nav');
  nav.className = 'chart-tools';
  nav.setAttribute('aria-label', 'Chart tools');

  if (active) {
    const who = document.createElement('p');
    who.className = 'chart-tools__who field-note';
    who.textContent = `Chart: ${active.name}${active.place?.name ? ` · ${active.place.name}` : ''}`;
    nav.append(who);
  }

  const ul = document.createElement('ul');
  ul.className = 'chart-tools__list';
  for (const item of CHART_TOOL_ROUTES) {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = id
      ? `#/${item.route}/${encodeURIComponent(id)}`
      : `#/${item.route}`;
    a.textContent = item.label;
    if (item.route === current) {
      a.setAttribute('aria-current', 'page');
      a.classList.add('is-current');
    }
    li.append(a);
    ul.append(li);
  }
  nav.append(ul);
  return nav;
}
